/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type * as React from 'react';

// eslint-disable-next-line import/no-cycle
import { DESKTOP_LIST_IMAGE_CONFIG } from '@coursera-web/cds-labs';

import { Typography2 } from '@coursera/cds-core';
import type {
  CarouselFilter as CarouselFilterTrackingData,
  Modal,
  PageConfigComponent as PageConfigComponentTrackingData,
  Recommender,
} from '@coursera/event-pulse-types';

import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { getCollectionType } from 'bundles/collections-common/utils/collectionsUtils';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import ProductCardPreviewImage from 'bundles/product-card/components/ProductCardPreviewImage';
import {
  convertDifficultyToEnum,
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import { PRODUCT_CARD_LAZY_LOAD_THRESHOLD } from 'bundles/product-card/utils/productCardUtils';
import { getTranslatedDifficulty } from 'bundles/search-common/constants';

export const collectionProductCardStyles = {
  /* TrackedLink2 adds an extra div that interferes with the card styling.
  This wrapper div applies style to that extra div to ensure the cards are styled correctly. */
  targetCardWrapper: css`
    height: 100%;

    > * {
      height: inherit;
    }

    .cds-ProductCard-list {
      /* temporarily force list card to have border */
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  desktopListCard: css`
    .cds-ProductCard-listPreviewImage {
      width: ${DESKTOP_LIST_IMAGE_CONFIG.width}px;
      height: ${DESKTOP_LIST_IMAGE_CONFIG.height}px;
      max-height: ${DESKTOP_LIST_IMAGE_CONFIG.height}px !important; /* override CDS */
    }
  `,
  sessionContent: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `,
};

export type CollectionProductCardEventingData = {
  searchQuery?: string;
  page?: string;
  filtersApplied?: Record<string, string[]>;
  searchIndex?: string;
  searchIndexPosition?: number;
  pageConfigComponent?: PageConfigComponentTrackingData;
  carouselFilter?: CarouselFilterTrackingData;
  modal?: Modal;
};

type Props = {
  item: CollectionEntity;
  itemIndex: number;
  itemRef?: React.RefObject<HTMLAnchorElement>;
  collection: CarouselCollection;
  collectionIndex?: number;
  variant: 'grid' | 'list';
  /** Sizes list preview images for a wide container rather than a phone. */
  isDesktopListView?: boolean;
  imageParams: { height: number; width?: number };
  trackingName: string;
  requireFullyVisibleCardTracking: boolean;
  /** Adds the search hit fields that search result pages report on card clicks. */
  includeSearchTrackingData?: boolean;
  eventingData?: CollectionProductCardEventingData;
  collectionRecommender?: Recommender;
  unifiedDiscoveryTrackingProperties: Record<string, unknown>;
};

/** A collection entity's product card, with the tracking data every collection surface reports. */
const CollectionProductCard = ({
  item,
  itemIndex,
  itemRef,
  collection,
  collectionIndex,
  variant,
  isDesktopListView = false,
  imageParams,
  trackingName,
  requireFullyVisibleCardTracking,
  includeSearchTrackingData = false,
  eventingData,
  collectionRecommender,
  unifiedDiscoveryTrackingProperties,
}: Props) => {
  const { collectionId, collectionTrackingData } = collection;
  const translatedDifficulty = getTranslatedDifficulty(item?.productDifficultyLevel ?? '');
  const translatedProductDifficultyLevel = !translatedDifficulty ? item?.productDifficultyLevel : translatedDifficulty;
  const section = collectionIndex !== undefined ? collectionIndex : collectionTrackingData?.collectionIndex;

  return (
    <div
      css={[
        collectionProductCardStyles.targetCardWrapper,
        isDesktopListView && collectionProductCardStyles.desktopListCard,
      ]}
    >
      <LearningProductCard
        ImageComponent={
          <ProductCardPreviewImage
            productType={item?.productType}
            imageUrl={item.imageUrl}
            enableLazyLoading={itemIndex > PRODUCT_CARD_LAZY_LOAD_THRESHOLD}
            enableFluidWidthForLazyImg
            imageParams={{
              ...imageParams,
              ...getImagePropOverrides(item.partners ?? [], item.imageUrl ?? ''),
            }}
          />
        }
        name={item.name || ''}
        titleComponent={item?.titleComponent || 'h3'}
        linkProps={{
          href: item?.link,
          target: item.forceOpenInNewTab ? '_blank' : undefined,
          component: TrackedLink2,
        }}
        // CDS renders its "Multiple educators" byline whenever partners.length !== 1, and an
        // empty array still mounts the byline. Pass undefined so a product with no partner
        // (e.g. a learning path) gets no byline at all.
        partners={
          item?.partners?.length
            ? getPartnerNamesAndLogos(item.partners, !!item?.coBrandingEnabled, item.partnerLogos)
            : undefined
        }
        autocompleteProductCardData={{
          cardImageUrl: item.imageUrl,
          partnerName: item?.partners?.[0]?.name,
          partnerLogo: item?.partnerLogos?.[0] ?? item?.partners?.[0]?.logo,
          difficulty: convertDifficultyToEnum(item?.productDifficultyLevel),
        }}
        skills={item?.skills}
        bodyContent={
          item?.session ? (
            <Typography2 component="p" variant="bodySecondary" css={collectionProductCardStyles.sessionContent}>
              <b>{item.session}</b>
            </Typography2>
          ) : null
        }
        numProductRatings={item?.numProductRatings}
        avgProductRating={item?.avgProductRating}
        productType={item?.productType}
        productDifficultyLevel={translatedProductDifficultyLevel}
        productDuration={item?.productDuration}
        statusTags={getProductCardStatusTags({
          isNew: item?.isNewContent,
          statusTags: item?.statusTags,
        })}
        badgeConfig={{
          productType: item?.productType,
          isPathwayContent: item?.isPathwayContent,
        }}
        postBadgeContent={item?.badges}
        variant={variant}
        id={item.id}
        slug={item?.slug}
        cardIndex={itemIndex}
        trackingProps={
          {
            trackedLinkProps: {
              trackingName,
              withVisibilityTracking: true,
              requireFullyVisible: requireFullyVisibleCardTracking,
              data: {
                itemIndex,
                collectionId,
                ...collectionTrackingData,
                ...(item.trackingData || { id: item.id, item }),
                collectionIndex: section,
                ...(includeSearchTrackingData && {
                  objectID: item.id || undefined,
                  hitPosition: itemIndex,
                  queryID: eventingData?.searchQuery || '',
                  query: eventingData?.searchQuery || '',
                  listIndex: Number(eventingData?.page) || 1,
                  noResults: false,
                  filtersApplied: eventingData?.filtersApplied,
                  indexName: eventingData?.searchIndex,
                  indexPosition: eventingData?.searchIndexPosition,
                }),
              },
              refAlt: itemRef,
            },
            additionalProductCardTrackingData: {
              pageConfigComponent: eventingData?.pageConfigComponent,
            },
            additionalV3TrackingData: {
              ...(eventingData?.modal ? { modal: eventingData.modal } : {}),
              carousel: {
                id: (collectionId as string) ?? '',
                name: collectionTrackingData?.collectionName ?? '',
                section,
                model: collectionId as string,
                type: getCollectionType(collectionId as string),
                recommender: collectionTrackingData?.carouselRecommender as Recommender | undefined,
                filter: eventingData?.carouselFilter,
              },
              ...unifiedDiscoveryTrackingProperties,
            },
          } as React.ComponentProps<typeof LearningProductCard>['trackingProps']
        }
        recommender={collectionRecommender}
        requireFullyVisibleCardTracking={requireFullyVisibleCardTracking}
      />
    </div>
  );
};

export default CollectionProductCard;
